import React, { useState, useEffect } from 'react';
import {
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import axiosInstance from '../utills/axiosInstance';
import moment from 'moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.vfs;

const PayslipDownload = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axiosInstance.get('/api/images');
      setEmployees(res.data || []);
    } catch (err) {
      setError('Failed to load employees');
    }
  };

  const fetchMonths = async (employeeId) => {
    if (!employeeId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(`/api/payroll/payslip/${employeeId}/months`);
      setMonths(res.data.months || []);
    } catch (err) {
      setError('No payslips found for this employee');
      setMonths([]);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!selectedEmployee || !selectedMonth) {
      setError('Please select employee and month');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const [monthName, yearStr] = selectedMonth.split(' ');
      const monthNum = moment.months().indexOf(monthName) + 1;
      const year = parseInt(yearStr);

      const res = await axiosInstance.get(
        `/api/payroll/payslip/${selectedEmployee}/${monthNum}/${year}`
      );

      const slip = res.data;

      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 60, 40, 60],
        header: {
          text: 'PAYSLIP',
          style: 'center',
          fontSize: 18,
          bold: true,
          margin: [0, 20, 0, 10],
          color: '#1976d2'
        },
        footer: (currentPage, pageCount) => ({
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: 'center',
          margin: [0, 30, 0, 0],
          fontSize: 10,
          color: '#666'
        }),
        content: [
          {
            columns: [
              {
                width: '60%',
                stack: [
                  { text: 'Employee Details', fontSize: 14 },
                  { text: `Name: ${slip.employee.firstName} ${slip.employee.lastName}`, margin: [0, 5] },
                  { text: `Employee ID: ${slip.employee.empId || 'N/A'}`, margin: [0, 3] },
                  { text: `Month: ${selectedMonth}`, margin: [0, 3] },
                ]
              },
              {
                width: '40%',
                alignment: 'right',
                stack: [
                  { text: 'Company Name', style: 'header' },
                  { text: 'Marsforce Pvt Ltd', margin: [0, 5] },
                  { text: 'Trichy', margin: [0, 3] },
                  { text: 'Tamilnadu, India', margin: [0, 3] },
                ]
              }
            ],
            margin: [0, 20]
          },

          {
            style: 'tableExample',
            table: {
              headerRows: 1,
              widths: ['*', 'auto'],
              body: [
                [{ text: 'Earnings', style: 'tableHeader' }, { text: 'Amount (₹)', style: 'tableHeader' }],
                ...slip.earnings.map(e => [e.name, e.amount.toFixed(2)]),
                [{ text: 'Total Earnings', bold: true }, { text: slip.net.toFixed(2), bold: true }]
              ]
            },
            margin: [0, 30, 0, 20]
          },

          {
            style: 'tableExample',
            table: {
              headerRows: 1,
              widths: ['*', 'auto'],
              body: [
                [{ text: 'Deductions', style: 'tableHeader' }, { text: 'Amount (₹)', style: 'tableHeader' }],
                ...slip.deductions.map(d => [d.name, d.amount.toFixed(2)]),
                [{ text: 'Total Deductions', bold: true }, { text: slip.totalDeductions.toFixed(2), bold: true }]
              ]
            },
            margin: [0, 0, 0, 30]
          },

          {
            columns: [
              {
                width: '60%',
                text: ''
              },
              {
                width: '40%',
                table: {
                  widths: ['*', 'auto'],
                  body: [
                    [{ text: 'Gross Pay', bold: true }, { text: `₹${slip.gross.toFixed(2)}`, bold: true }],
                    [{ text: 'Total Deductions', bold: true }, { text: `₹${slip.totalDeductions.toFixed(2)}`, bold: true }],
                    [{ text: 'Net Pay (Take Home)', bold: true, color: '#1976d2', fontSize: 16 },
                    { text: `₹${slip.net.toFixed(2)}`, bold: true, color: '#1976d2', fontSize: 16 }]
                  ]
                },
                layout: {
                  fillColor: (rowIndex) => rowIndex === 2 ? '#e3f2fd' : null
                }
              }
            ]
          },

          { text: 'Thank you for your hard work!', style: 'footerText', margin: [0, 40, 0, 0] }
        ],
        styles: {
          header: { fontSize: 18, bold: true, margin: [0, 0, 0, 10] },
          tableExample: { margin: [0, 5, 0, 15] },
          tableHeader: { bold: true, fontSize: 13, color: 'white', fillColor: '#1976d2' },
          footerText: { alignment: 'center', italics: true, color: '#666' }
        },
        defaultStyle: { fontSize: 11 }
      };

      const pdfName = `Payslip_${slip.employee.firstName}_${slip.employee.lastName}_${selectedMonth.replace(' ', '_')}.pdf`;
      pdfMake.createPdf(docDefinition).download(pdfName);

      setSuccess('Payslip downloaded successfully');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Failed to generate payslip');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 5, maxWidth: 500, mx: 'auto', mt: 5, boxShadow: 5 }}>
      <Typography variant="h5" gutterBottom textAlign="center">
        Download Payslip
      </Typography>

      <Box sx={{ mt: 4 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Employee</InputLabel>
          <Select
            value={selectedEmployee}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedEmployee(id);
              setSelectedMonth('');
              setMonths([]);
              if (id) fetchMonths(id);
            }}
            label="Select Employee"
          >
            <MenuItem value="">Choose Employee</MenuItem>
            {employees.map(emp => (
              <MenuItem key={emp._id} value={emp._id}>
                {emp.firstName} {emp.lastName} ({emp.empId || 'No ID'})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 4 }}>
          <InputLabel>Select Month</InputLabel>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            label="Select Month"
            disabled={!selectedEmployee}
          >
            <MenuItem value="">Select Month</MenuItem>
            {months.map(m => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Button
          variant="contained"
          color="success"
          size="large"
          fullWidth
          onClick={generatePDF}
          disabled={!selectedEmployee || !selectedMonth || loading}
          sx={{ py: 2, fontSize: '1.1rem' }}
        >
          {loading ? 'Generating PDF...' : 'Download Payslip PDF'}
        </Button>
      </Box>
    </Paper>
  );
};

export default PayslipDownload;
